import { useState } from 'preact/hooks';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import type { RatedProductWire } from 'shared';
import { Button } from '@/components/button.js';
import { OptionList } from '@/flows/marketplace/components/option-list.js';
import { ProductDescription } from '@/components/product-description.js';
import { SurchargeToggle } from '@/components/surcharge-toggle.js';
import { formatUsd } from 'shared';
import { tierTermLabel } from '@/lib/formatters.js';
import { useProductCard } from '@/lib/use-product-card.js';
import type { CartSurcharge } from '@/lib/widget-types.js';

interface TierCardProps {
  /** `quote_item_id`s of this product's tiers, ascending by term_months. */
  quoteItemIds: string[];
  expanded: boolean;
  onToggleExpand: () => void;
  /** Fired with the new cart index — the screen sends the customer to this plan's payment step. */
  onSelected: (cartIndex: number) => void;
}

export function TierCard({ quoteItemIds, expanded, onToggleExpand, onSelected }: TierCardProps) {
  const model = useProductCard(quoteItemIds);
  const [addonsExpanded, setAddonsExpanded] = useState(false);
  if (model === null) return null;

  const {
    termTiers,
    currentTier,
    productName,
    productDescription,
    isInCart,
    activeSurcharges,
    currentSubtotal,
    lowestSubtotal,
    subtotalFor,
    termMonthsLabel,
    coverageMilesLabel,
    deductibleLabel,
    getSurchargeSelected,
    toggleSurcharge,
    handleTermChange,
    handleSelectPlan,
    handleRemove,
  } = model;

  const summaryMeta = isInCart ? (
    <span className="sm-stack sm-tabular sm-pair-end sm-product-meta" data-tone="selected">
      <span className="sm-type-heading sm-amount">{formatUsd(currentSubtotal)}</span>
      <span className="sm-type-caption sm-muted sm-product-meta-detail">
        {summaryDetail(currentTier, activeSurcharges)}
      </span>
    </span>
  ) : lowestSubtotal !== null ? (
    <span className="sm-stack sm-tabular sm-pair-end sm-product-meta">
      <span className="sm-type-eyebrow">From</span>
      <span className="sm-type-heading sm-amount">{formatUsd(lowestSubtotal)}</span>
    </span>
  ) : null;

  const addonCount = currentTier.surcharges.length;

  return (
    <div className="sm-product" data-state={isInCart ? 'selected' : undefined}>
      <button
        type="button"
        className="sm-summary-row sm-product-summary"
        aria-expanded={expanded}
        onClick={onToggleExpand}
      >
        <span className="sm-type-heading sm-product-name">
          {isInCart && (
            <span className="sm-center sm-check-disc" aria-hidden="true">
              <CheckIcon size={12} strokeWidth={3} />
            </span>
          )}
          <span className="sm-product-name-text">{productName}</span>
        </span>
        {summaryMeta}
        <ChevronDownIcon className="sm-chevron" size={18} aria-hidden="true" />
      </button>

      {expanded && (
        <div className="sm-stack sm-product-body">
          <ProductDescription doc={productDescription} />

          <div className="sm-stack sm-term-group">
            {termTiers.length > 1 && (
              <>
                <p className="sm-type-eyebrow">Choose a term</p>
                <OptionList<number>
                  ariaLabel="Choose a term"
                  value={currentTier.term_months}
                  options={termTiers.map((t) => ({
                    value: t.term_months,
                    label: tierTermLabel(t),
                    detail: formatUsd(subtotalFor(t)),
                  }))}
                  onChange={handleTermChange}
                />
              </>
            )}

            <div className="sm-stack sm-term-detail">
              {[
                ['Term', termMonthsLabel],
                ['Coverage', coverageMilesLabel],
                ['Deductible', deductibleLabel],
              ].map(([label, value]) => (
                <div className="sm-type-body sm-tabular sm-totals-row" key={label}>
                  <span>{label}</span>
                  <span className="sm-type-body-strong">{value}</span>
                </div>
              ))}
              <div className="sm-stack sm-totals">
                <div className="sm-type-heading sm-tabular sm-totals-row" data-tone="total">
                  <span>Subtotal</span>
                  <span>{formatUsd(currentSubtotal)}</span>
                </div>
              </div>
            </div>
          </div>

          {addonCount > 0 && (
            <div className="sm-stack sm-customize">
              <button
                type="button"
                className="sm-type-body-strong sm-customize-toggle"
                aria-expanded={addonsExpanded}
                onClick={() => setAddonsExpanded((v) => !v)}
              >
                Add-ons
                <span className="sm-type-eyebrow sm-customize-toggle-count">{addonCount}</span>
                <ChevronDownIcon
                  className="sm-customize-toggle-chevron"
                  size={14}
                  aria-hidden="true"
                />
              </button>
              {addonsExpanded && (
                <div className="sm-stack">
                  {currentTier.surcharges.map((s) => (
                    <SurchargeToggle
                      key={s.code}
                      description={s.description}
                      basePrice={s.base_price}
                      optional={s.optional}
                      selected={s.optional ? getSurchargeSelected(s.code) : true}
                      onToggle={() => toggleSurcharge(s.code)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="sm-product-actions">
            <Button
              onClick={() => {
                if (isInCart) {
                  handleRemove();
                  return;
                }
                const cartIndex = handleSelectPlan();
                if (cartIndex !== null) onSelected(cartIndex);
              }}
              variant={isInCart ? 'secondary' : 'primary'}
              endIcon={null}
            >
              {isInCart ? 'Remove selection' : 'Select plan'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function summaryDetail(tier: RatedProductWire, surcharges: CartSurcharge[]): string {
  const term = tierTermLabel(tier);
  const addOnCount = surcharges.filter((s) => s.selected).length;
  if (addOnCount === 0) return term;
  if (addOnCount === 1) return `${term}, 1 add-on`;
  return `${term}, ${addOnCount} add-ons`;
}
